/**
 * Sign requests (gap 3 of docs/listings/processes/ba-checklists-vs-app.md: the
 * BA Signage checklist and its Sign Request spreadsheet). A listing keeps its
 * sign requests (listing_sign_requests, migration 20261017234905): an SR number,
 * the sign package, Kyle's approval when it is needed, the form's details, and
 * the status DRAFT → APPROVED (only when approval is needed) → SENT (to
 * Infinity) → INSTALLED. The API and the web form share the rules below.
 *
 * The package and status codes are CODE: the database allows exactly these
 * (chk_listing_sign_requests_sign_package / _status); the labels can change.
 */
/** The packages, in the picker's order. Only STANDARD goes out without Kyle's approval. */
export const SIGN_PACKAGES = [
    { code: 'STANDARD', label: 'Standard: one 4x4, name rider, For Sale / For Lease topper' },
    { code: 'LARGER', label: 'Larger than 4x4' },
    { code: 'MULTIPLE', label: 'More than one sign' },
    { code: 'REPLACEMENT', label: 'Replacement sign' },
    { code: 'OTHER', label: 'Other' },
];
export const SIGN_PACKAGE_CODES = SIGN_PACKAGES.map((p) => p.code);
/** A new request's package (the guaranteed one). */
export const DEFAULT_SIGN_PACKAGE = 'STANDARD';
export const isSignPackage = (code) => typeof code === 'string' && SIGN_PACKAGE_CODES.includes(code);
export const signPackageLabel = (code) => SIGN_PACKAGES.find((p) => p.code === code)?.label ?? code ?? '';
/**
 * Whether a request needs Kyle's approval: always when the package is not the
 * standard one, else when someone flagged it (`flagged`, e.g. a cell phone on
 * the sign). chk_listing_sign_requests_approval_needed holds the same rule.
 */
export const signRequestNeedsApproval = (pkg, flagged) => pkg !== 'STANDARD' || flagged;
/** The status chain (listing_sign_requests.status). */
export const SIGN_REQUEST_STATUSES = ['DRAFT', 'APPROVED', 'SENT', 'INSTALLED'];
export const SIGN_REQUEST_STATUS_LABEL = {
    DRAFT: 'Draft',
    APPROVED: 'Approved',
    SENT: 'Sent to Infinity',
    INSTALLED: 'Installed',
};
export const isSignRequestStatus = (code) => typeof code === 'string' && SIGN_REQUEST_STATUSES.includes(code);
/**
 * The next step of a request, or null when it is installed: approval first when
 * it needs one and has none, then sent, then installed.
 */
export function nextSignRequestStep(req) {
    if (req.status === 'DRAFT')
        return req.needs_approval ? 'APPROVE' : 'SEND';
    if (req.status === 'APPROVED')
        return 'SEND';
    if (req.status === 'SENT')
        return 'INSTALL';
    return null;
}
/** Bounds the API enforces and the web form checks first (chk_listing_sign_requests_* mirror them). */
export const SIGN_REQUEST_LIMITS = {
    /** SR numbers run 1 to this (the setting listings.signage.sr_start_number too). */
    maxSrNumber: 999999,
    signDetails: 200,
    approvedByName: 100,
    installType: 100,
    installOn: 100,
    googleLink: 2000,
    notes: 2000,
};
/** The documents a sign request keeps (agreement document kinds): the location screenshot, the install photos. */
export const SIGN_REQUEST_FILE_KINDS = ['SIGN_REQUEST', 'SIGN_INSTALL_PHOTOS'];
export const isSignRequestFileKind = (code) => typeof code === 'string' && SIGN_REQUEST_FILE_KINDS.includes(code);
/** 7 → "SR-0007"; past four digits it just grows (SR-12345). */
export const formatSrNumber = (n) => `SR-${String(n).padStart(4, '0')}`;
//# sourceMappingURL=sign-requests.js.map